/**
 * Product rail resilience: each rail already server-renders successfully as
 * part of the normal page response (skeleton/error markup ship hidden by
 * default, so a no-JS visit and SEO crawlers see real content immediately -
 * there is no artificial loading flash on a normal page load). What this
 * adds is a genuine recovery path for the one thing that CAN legitimately
 * go wrong later: `[data-rail-retry]` re-fetches just this section via
 * `?section_id=`, so a rail that ends up in its error state (for example,
 * after `assets/cart.js`'s Section Rendering replaces it following a cart
 * change and something failed) can recover without reloading the whole
 * page or taking any other section down with it.
 *
 * DOM contract: `[data-rail]` carries `data-section-id`; its children
 * `[data-rail-content]` (header + items), `[data-rail-skeleton]`, and
 * `[data-rail-error]` (with `[data-rail-retry]`) are toggled here.
 */
function setRailState(rail, state) {
  const content = rail.querySelectorAll('[data-rail-content]');
  const skeleton = rail.querySelector('[data-rail-skeleton]');
  const error = rail.querySelector('[data-rail-error]');

  content.forEach((el) => {
    el.hidden = state !== 'content';
  });
  if (skeleton) skeleton.hidden = state !== 'loading';
  if (error) error.hidden = state !== 'error';
}

async function retryRail(rail) {
  setRailState(rail, 'loading');

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', rail.dataset.sectionId);
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextRail = parsed.querySelector(`[data-section-id="${CSS.escape(rail.dataset.sectionId)}"]`);
    if (!nextRail) throw new Error('missing-section-root');

    rail.replaceWith(nextRail);
    window.dispatchEvent(new CustomEvent('vellum:cards-updated'));
  } catch (error) {
    setRailState(rail, 'error');
  }
}

document.addEventListener('click', (event) => {
  const retryButton = event.target.closest('[data-rail-retry]');
  if (!retryButton) return;

  const rail = retryButton.closest('[data-rail]');
  if (rail) retryRail(rail);
});
